/* Core & libraries */
import React, {useEffect, useRef, useState} from "react";
import { useTheme } from "react-jss";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { ErrorMessage, Field, Formik } from "formik";
import { Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router";

/* Helpers, constants, custom methods, internal libraries */
import {
  useValidationService,
  createValidationPolicyYupSchema
} from "../../../resources/validations/validations";
import { ReactComponent as UserDeleteIcon } from "../../../resources/images/user-x.svg";
import { usePeopleService } from "../../../services/peopleService";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useUserContext } from "../../../contexts/userContext";
import moment from 'moment/moment';
import { ADMIN_COMPANY_DETAILS_ROUTE } from '../../../constants/routes';
import { Link } from 'react-router-dom';
import { isAdmin } from '../../../helpers/general';

/* Components */
import UCSelect from "../../ucselect";
import UCButton from "../../buttons";
import DeleteActionModal from "../../modals/deleteActionModal";
import WarningModal from "../../modals/warningModal";
import { ReactComponent as DeleteUserIconRed } from "../../../resources/images/user-icon-red.svg";
import { ReactComponent as HelpIcon } from "../../../resources/images/help.svg";
import { ReactComponent as EditEmailSvg } from '../../../resources/images/edit.svg';
import UpdateEmailModal from "../../modals/updateEmailModal";

/* Styles */
import styles from "./styles.js"
import { colors } from "../../../constants/colors";
import generalStyles from "../../../resources/styles/general";


const messages = defineMessages({
  firstNamePlaceholder: {
    id: 'traveler.details.firstNamePlaceholder'
  },
  lastNamePlaceholder: {
    id: 'traveler.details.lastNamePlaceholder'
  },
  emailPlaceholder: {
    id: 'traveler.details.emailPlaceholder'
  },
  membershipNumberPlaceholder: {
    id: 'traveler.details.membershipNumberPlaceholder.AC'
  },
  companyNamePlaceholder: {
    id: 'traveler.details.companyName.AC'
  },
})

const UserProfileAC = ({travelerDetails, roles, teams, refetchTravelerDetails}) => {
  const theme = useTheme();
  const classes = styles({theme});
  const general = generalStyles({ theme });
  const intl = useIntl();
  const navigate = useNavigate();

  const formRef = useRef(null);
  
  const { travelerDetailsMockedApiValidationObj } = useValidationService()
  const [travelerSchema, setTravelerSchema] = useState({});

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRemoveAdminOnlyModal, setShowRemoveAdminOnlyModal] = useState(false);
  const [showRemoveOwnerOnlyModal, setShowRemoveOwnerOnlyModal] = useState(false);
  const [showRemoveCitiOnlyModal, setShowRemoveCitiOnlyModal] = useState(false);
  const [showUpdateEmailModal, setShowUpdateEmailModal] = useState(false);

  const { deletePeople, updatePeople } = usePeopleService();
  const { checkPermission } = useHasPermission();
  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  const adminUser = isAdmin(userDetails);

  useEffect(() => {
    (async () => {
      //TODO: api call to get validations //simpleMockedApiValidationObj
      const result = createValidationPolicyYupSchema(travelerDetailsMockedApiValidationObj);
      setTravelerSchema(result)
    })();
  }, []);

  const goBack = () => {
    navigate(-1);
  }

  const deleteAction = async () => {
    const response = await deletePeople({
      entityID: travelerDetails?.userEntityId,
      lastName: travelerDetails?.lastName,
      firstName: travelerDetails?.firstName,
    })
    if (response) {
      setShowDeleteModal(false)
      goBack();
    }
  }

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit()
    }
  }

  const submitForm = async (values) => {
    await updatePeople({
      userId: travelerDetails?.userEntityId,
      roleId: values?.roleId,
      groupEntityId: values?.groupId,
    })
  }

  const checkCanChangeRole = () => {
    if(!travelerDetails?.canChangeRole) {    
      setShowRemoveOwnerOnlyModal(true)
    }
  }

  const checkIfCanRemoveUser = () => {
    if(travelerDetails?.canRemoveUser) {
      setShowDeleteModal(true);
    } else if(travelerDetails?.companyInfo?.companyType ==="Citi"){
      setShowRemoveCitiOnlyModal(true);
    } else {
      setShowRemoveAdminOnlyModal(true);
    }
  }

  return (
    <div className={classes.personalInfoContainer}>
      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={{
          firstName: travelerDetails?.firstName,
          lastName: travelerDetails?.lastName,
          roleId: travelerDetails?.companyInfo?.roleId,
          groupId: travelerDetails?.companyInfo?.groupEntityId,
          emailAddress: travelerDetails?.emailAddress,
          membershipNumber: travelerDetails?.membershipNumber ? `#${travelerDetails?.membershipNumber}` : "-",
          companyName: travelerDetails?.companyInfo?.companyName,
          termsAcceptedOn: travelerDetails?.termsAcceptedInfo?.termsAcceptedOn
            ? (() => {
                const parts = travelerDetails.termsAcceptedInfo.termsAcceptedOn.split(' ');
                return parts.length >= 2 ? `${parts[0]} · ${parts[1]} UTC` : travelerDetails.termsAcceptedInfo.termsAcceptedOn;
              })()
            : '',
        }}
        validationSchema={travelerSchema}
        onSubmit={(values, { setSubmitting }) => {
          submitForm(values, setSubmitting);
        }}
      >
        {({
            values,
            errors,
            touched,
            setFieldTouched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue
          }) => {
          return (
            <>
              <form onSubmit={handleSubmit}>
                <div className="row formDetailsBlock">
                  <div className="col-12 col-md-4 descText">
                    <div className={classes.personalDetails}>
                      <FormattedMessage id="traveler.details.personal.AC"/>
                    </div>
                    <div className={classes.description}>
                      <FormattedMessage id="traveler.details.descr.AC"/>
                    </div>
                  </div>
                  <div className="col-12 col-md-8">
                    <div className={classes.formContainer}>
                      <Row className={classes.personalDetailsRow}>
                        <Col xs={12} sm={6}>
                          <div className={`${classes.inputContainer}`}>
                            <label htmlFor="firstName" className={classes.label}>
                              <FormattedMessage id="traveler.details.firstName"/>
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
                              type="text"
                              name="firstName"
                              id="firstName"
                              className={
                                errors.firstName && touched.firstName
                                  ? "input-error"
                                  : null
                              }
                              disabled={true}
                            />
                            <ErrorMessage
                              name="firstName"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                        </Col>
                        <Col xs={12} sm={6}>
                          <div className={`${classes.inputContainer}`}>
                            <label htmlFor="lastName" className={classes.label}>
                              <FormattedMessage id="traveler.details.lastName"/>
                            </label>
                            <Field
                              placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
                              type="text"
                              name="lastName"
                              id="lastName"
                              className={
                                errors.lastName && touched.lastName
                                  ? "input-error"
                                  : null
                              }
                              disabled={true}
                            />
                            <ErrorMessage
                              name="lastName"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                        </Col>
                      </Row>
                      <div className={`${classes.description} ${classes.rolesWrapper}`}>
                        <FormattedMessage id="traveler.details.note.AC"/>
                      </div>
                      <Row>
                        <div className={classes.inputMailContainer}>
                          <label htmlFor="emailAddress" className={classes.label}>
                            <FormattedMessage id="traveler.details.emailAddress"/>
                          </label>
                          <div className={classes.editDiv}>
                            <Field
                              placeholder={intl.formatMessage(messages.emailPlaceholder)}
                              type="text"
                              name="emailAddress"
                              id="emailAddress"
                              className={
                                errors.emailAddress && touched.emailAddress
                                  ? "input-error"
                                  : null
                              }
                              disabled={true}
                            />
                            {checkPermission("F_EDIT_MY_EMAILADDRESS" || "F_EDIT_OTHERS_EMAILADDRESS") &&
                              <OverlayTrigger
                                placement="top"
                                trigger={!travelerDetails?.allowEditEmail ? ['focus', 'click'] : []}
                                overlay={
                                  <Tooltip id="email" className={`${general.main} ${classes.emailTooltip}`}>
                                    <div><FormattedMessage id="updateEmail.tooltip"/></div>
                                  </Tooltip>
                                }
                              >
                                <span>
                                  <UCButton
                                    onClick={() => setShowUpdateEmailModal(true)}
                                    className={classes.editButton}
                                    iconEnd={<EditEmailSvg/>}
                                    disabled={!travelerDetails?.allowEditEmail || travelerDetails?.userStatus === "REMOVED"}
                                  />
                                </span>
                              </OverlayTrigger>
                            }
                          </div>
                          <ErrorMessage
                            name="emailAddress"
                            component="span"
                            className={classes.error}
                          />
                        </div>
                      </Row>
                    </div>
                  </div>
                </div>

                <div className={classes.dividerContainer}>
                  <hr className={classes.border}/>
                </div>

                <div className="row formDetailsBlock">
                  <div className="col-12 col-md-4 descText">
                    <div className={classes.personalDetails}>
                      <FormattedMessage id="traveler.details.membership.AC"/>
                    </div>
                    <div className={classes.description}>
                      <FormattedMessage id="traveler.details.desc.AC"/>
                    </div>
                  </div>
                  <div className="col-12 col-md-8">
                    <div className={classes.formContainer}>
                      {
                        adminUser &&
                        <Row>
                          <div className={`${classes.inputContainer} ${classes.rolesWrapper}`}>
                            <label htmlFor="companyName" className={classes.label}>
                              <FormattedMessage id="traveler.details.companyName.AC"/>
                            </label>
                            <Link to={ADMIN_COMPANY_DETAILS_ROUTE.replace(":companyEntityId", travelerDetails?.companyInfo?.companyEntityId)} className={classes.link}>
                              <Field
                                placeholder={intl.formatMessage(messages.companyNamePlaceholder)}
                                type="text"
                                name="companyName"
                                id="companyName"
                                className={`${errors.companyName && touched.companyName ? "input-error" : ""} ${classes.companyNameField}`}
                                disabled={true}
                              />
                            </Link>
                            <ErrorMessage
                              name="companyName"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                        </Row>
                      }
                      <Row>
                        <div className={classes.inputContainer}>
                          <label htmlFor="roleId" className={classes.label}>
                            <FormattedMessage id="traveler.details.role"/>
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id="tooltipRole" className={`${general.main} ${classes.tooltip}`}>
                                  <div className={classes.roleTooltipTitle}><FormattedMessage
                                    id="traveler.details.roleTooltipTitle"/></div>
                                  <div className={classes.roleTooltipAdmin}><FormattedMessage
                                    id={`traveler.details.roleTooltipTravelArranger.${providerCode}`} /></div>
                                  <div className={classes.roleTooltipAdmin}><FormattedMessage
                                    id={`traveler.details.roleTooltipAdmin.${providerCode}`}/></div>
                                  <div className={classes.roleTooltipItem}><FormattedMessage
                                    id={`traveler.details.roleTooltipTraveler.${providerCode}`}/></div>
                                </Tooltip>
                              }
                            >
                              <HelpIcon className={classes.helpIcon}/>
                            </OverlayTrigger>
                          </label>
                          {
                              roles.length > 0 &&
                              <Field
                                type="select"
                                name="roleId"
                                id="roleId"
                                render={({ form }) => (
                                  <div className={classes.rolesWrapper}>
                                    <UCSelect
                                      onMenuOpen={() => checkCanChangeRole()}
                                      className={classes.roles}
                                      displayName={<FormattedMessage id="people.allRoles" />}
                                      options={roles}
                                      defaultValue={roles.find(item => parseInt(item.roleId) === parseInt(values.roleId))}
                                      onChange={(item) => form.setFieldValue("roleId", item.roleId)}
                                      getOptionLabel={(option) => option.roleName}
                                      getOptionValue={(option) => option.roleId}
                                      disabled={!checkPermission("F_EDIT_USER") || travelerDetails?.userStatus === "REMOVED"}
                                  />
                                  <ErrorMessage
                                    name="roleId"
                                    component="span"
                                    className={classes.error}
                                  />
                                </div>
                              )}
                            />
                          }
                        </div>
                      </Row>
                      <Row>
                        <div className={classes.inputContainer}>
                          <label htmlFor="groupId" className={classes.label}>
                            <FormattedMessage id="traveler.details.team"/>
                          </label>
                          {
                            teams.length > 0 &&
                            <Field
                              type="select"
                              name="groupId"
                              id="groupId"
                              render={({ form }) => (
                                <div className={classes.rolesWrapper}>
                                  <UCSelect
                                    className={classes.roles}
                                    displayName={<FormattedMessage id="people.allTeams" />}
                                    options={teams}
                                    defaultValue={teams.find(item => item.groupEntityId === values.groupId)}
                                    onChange={(item) => form.setFieldValue("groupId", item.groupEntityId)}
                                    getOptionLabel={option => option.groupName}
                                    getOptionValue={option => option.groupEntityId}
                                    disabled={!checkPermission("F_EDIT_USER") || travelerDetails?.userStatus === "REMOVED"}
                                  />
                                  <ErrorMessage
                                    name="groupId"
                                    component="span"
                                    className={classes.error}
                                  />
                                </div>
                              )}
                            />
                          }
                        </div>
                      </Row>
                      <Row>
                        <div className={classes.inputContainerMembership}>
                          <label htmlFor="membershipNumber" className={classes.label}>
                            <FormattedMessage id="traveler.details.membershipNumber.AC"/>
                          </label>
                          <div>
                            <Field
                              placeholder={intl.formatMessage(messages.membershipNumberPlaceholder)}
                              type="text"
                              name="membershipNumber"
                              id="membershipNumber"
                              className={
                                errors.membershipNumber && touched.membershipNumber
                                  ? "input-error"
                                  : null
                              }
                              disabled={true}
                            />
                          </div>
                          <ErrorMessage
                            name="membershipNumber"
                            component="span"
                            className={classes.error}
                          />
                        </div>
                      </Row>
                      <Row>
                        <div className={classes.inputContainer}>
                          <label htmlFor="termsAcceptedOn" className={classes.label}>
                            <FormattedMessage id="traveler.details.termsAcceptedOn.AC"/>
                          </label>
                          <div>
                            <Field
                              type="text"
                              name="termsAcceptedOn"
                              id="termsAcceptedOn"
                              disabled={true}
                            />
                          </div>
                        </div>
                      </Row>
                    </div>
                  </div>
                </div>

                <div className={classes.dividerContainer}>
                  <hr className={classes.border}/>
                </div>

                <Row>
                  <Col>
                    <div className={classes.btnContainer}>
                      {
                        travelerDetails?.userStatus === "REMOVED" ?
                          <div className={classes.removedDate}>
                            <FormattedMessage id="traveler.details.removedOnDate" values={{date: moment(travelerDetails?.userStatusChangedOn).format("MM/DD/YYYY")}}/>
                          </div>
                          :
                          checkPermission("F_DELETE_USER") &&
                          <UCButton
                            type="deleteLink"
                            className={classes.button}
                            text={<FormattedMessage id="traveler.details.removeEmployee"/>}
                            onClick={() => checkIfCanRemoveUser()}
                            icon={<DeleteUserIconRed />}
                          />
                      }
                   </div>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div className={classes.buttonContainer}>
                      <div className={classes.btnContainer}>
                        <UCButton
                          type="default"
                          className={classes.button}
                          text={<FormattedMessage id="traveler.details.cancel"/>}
                          onClick={goBack}
                        />
                        {
                          travelerDetails?.userStatus === "ACTIVE" &&
                          (checkPermission("F_EDIT_USER") ||
                            (travelerDetails?.userEntityId === userDetails?.userInfo?.userEntityId && checkPermission("F_EDIT_MYUSER"))
                          ) &&
                          <UCButton
                            type="primary"
                            onClick={handleFormSubmit}
                            text={<FormattedMessage id="traveler.details.save"/>}
                            spinner={{
                              name: "updatePeopleSpinner",
                              size: 20,
                              color: colors.white
                            }}
                          />
                        }
                      </div>
                    </div>
                  </Col>
                </Row>
              </form>
            </>
          )
        }
        }
      </Formik>
      <DeleteActionModal
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        modalIcon={ <UserDeleteIcon /> }
        modalTitle={ <FormattedMessage id="peopleGrid.deleteAction.title" values={{name: `${travelerDetails?.firstName } ${travelerDetails?.lastName }`}}/> }
        modalDescription={ <FormattedMessage id="peopleGrid.deleteAction.description" /> }
        submitCallback={deleteAction}
        setSelectedRow={(value) => console.log(value)}
      />
      <WarningModal
        showModal={showRemoveAdminOnlyModal}
        setShowModal={setShowRemoveAdminOnlyModal}
        modalIcon={ <UserDeleteIcon /> }
        modalTitle={ <FormattedMessage id="removeAdminOnlyModal.title"/> }
        modalDescription={ <FormattedMessage id="removeAdminOnlyModal.description" /> }
      />
      
      <WarningModal
        showModal={showRemoveOwnerOnlyModal}
        setShowModal={setShowRemoveOwnerOnlyModal}
        modalIcon={ <UserDeleteIcon /> }
        modalTitle={ <FormattedMessage id="removeOwnerOnlyModal.title"/> }
        modalDescription={ 
          <div className={classes.changeRoleModal}>
            <p><FormattedMessage id="removeOwnerOnlyModal.description1" /></p>
            <p><FormattedMessage id="removeOwnerOnlyModal.description2" /></p>
          </div> 
        }
      />      
      <WarningModal
        showModal={showRemoveCitiOnlyModal}
        setShowModal={setShowRemoveCitiOnlyModal}
        modalIcon={ <UserDeleteIcon /> }
        modalTitle={ <FormattedMessage id="removeCitiUser.title"/> }
        modalDescription={ <FormattedMessage id="removeCitiOnlyModal.description" /> }
      />
      <UpdateEmailModal
        setShowUpdateEmailModal={setShowUpdateEmailModal}
        showUpdateEmailModal={showUpdateEmailModal}
        travelerDetails={ travelerDetails }
        refetchTravelerDetails= { refetchTravelerDetails }
      />
    </div>
  )
}

export default UserProfileAC;